// Reason-specific copy for the unavailable estimate/quote states.
// Maps the backend reason_codes (estimate_orchestrator -> zone_engine / cargo guard /
// pricing engine) to logistics-grade, reason-specific UI copy. Display-only; no new
// workflow. Used by the public Check Price drawer and the authenticated NewBooking flow.

// Geocoding / Maps / terminal-resolution failures -> "could not verify this address".
const ADDRESS_VERIFY_CODES = [
  "ADDRESS_UNRESOLVED",
  "GOOGLE_DISTANCE_FAILED",
  "GOOGLE_API_KEY_MISSING",
  "TERMINAL_ANCHOR_NOT_CONFIGURED",
];

// Cargo-acceptance / pricing limits tied to the package profile.
const PACKAGE_CODES = [
  "BULKY_OR_OVERSIZED",
  "PACKAGE_TOO_HEAVY_PER_PIECE",
  "PACKAGE_DIMENSIONS_EXCEED_CONFIGURED_LIMIT",
  "PACKAGE_DIMENSIONS_REQUIRE_AIRLINE_REVIEW",
  "AIRLINE_ACCEPTANCE_LIMITS_UNCONFIRMED",
  "IRREGULAR_SHAPE_REVIEW",
  "NON_STACKABLE_REVIEW",
  "FRAGILE_REVIEW",
  "PACKAGE_REQUIRES_SPECIAL_HANDLING",
  "UNSUPPORTED_GOODS_CATEGORY",
  "RESTRICTED_GOODS_REVIEW",
  "PROHIBITED_GOODS_NOT_SERVICEABLE",
  "INVALID_INPUT",
];

/**
 * Map a list of backend reason codes to reason-specific unavailable-state copy.
 * Precedence: route -> address-verification -> pickup -> delivery -> zone-D pair ->
 * package profile -> generic fallback.
 * @returns {{key, title, body, cta, secondary}}
 */
export function mapUnavailableReason(reasonCodes = []) {
  const set = new Set(reasonCodes || []);
  const has = (c) => set.has(c);
  const hasAny = (arr) => arr.some((c) => set.has(c));

  // 1) Route inactive / lane not currently served
  if (has("NO_ACTIVE_PRIORITY_ROUTE")) {
    return {
      key: "route_inactive",
      title: "This lane is not currently available",
      body: "Air12 is currently accepting bookings only on active Priority Routes. Please request a callback and our team will let you know when this lane opens.",
      cta: "Request callback",
      secondary: "Edit details",
    };
  }

  // 5) Address could not be verified / Maps / geocoding failure
  if (hasAny(ADDRESS_VERIFY_CODES)) {
    return {
      key: "address_unverified",
      title: "We could not verify this address",
      body: "Please check the address or pincode and try again. If the address is correct, request a callback and our team will help verify serviceability.",
      cta: "Request callback",
      secondary: "Edit address",
    };
  }

  // 2) Pickup address outside current service area
  if (has("NO_SERVICEABLE_ORIGIN_AIRPORT")) {
    return {
      key: "pickup_out_of_area",
      title: "Pickup address is outside our current service area",
      body: "We currently support pickup locations within the active Air12 service radius for this route. Please edit the pickup address or request a callback.",
      cta: "Request callback",
      secondary: "Edit pickup address",
    };
  }

  // 3) Delivery address outside current service area
  if (has("NO_SERVICEABLE_DESTINATION_AIRPORT")) {
    return {
      key: "delivery_out_of_area",
      title: "Delivery address is outside our current service area",
      body: "We currently support delivery locations within the active Air12 service radius for this route. Please edit the delivery address or request a callback.",
      cta: "Request callback",
      secondary: "Edit delivery address",
    };
  }

  // 4) Both pickup/delivery outside service radius or Zone D (pricing-stage)
  if (has("ZONE_D")) {
    return {
      key: "address_pair_out_of_area",
      title: "This address pair is outside our current service area",
      body: "Air12 is not currently able to auto-price this pickup and delivery combination. Please request a callback if you would like our team to review service availability.",
      cta: "Request callback",
      secondary: "Edit details",
    };
  }

  // 6) Package profile outside instant-estimate capability
  if (hasAny(PACKAGE_CODES)) {
    return {
      key: "package_profile",
      title: "This package profile is outside our current instant-estimate limits",
      body: "Based on the package weight, dimensions, or handling requirements entered, Air12 cannot auto-price this shipment right now. You may edit the package details or request a callback.",
      cta: "Request callback",
      secondary: "Edit package details",
    };
  }

  // 8) Generic fallback (unknown reason)
  return {
    key: "generic",
    title: "We cannot auto-price this shipment yet",
    body: "Air12 could not generate an instant estimate for these details. Please edit the shipment details or request a callback.",
    cta: "Request callback",
    secondary: "Edit details",
  };
}

// 7) Load Assist unavailable while base cargo is serviceable — inline note only;
//    never blocks the base estimate/quote.
export const LOAD_ASSIST_UNAVAILABLE_TITLE =
  "Loading/unloading assistance is not available for this package profile";
export const LOAD_ASSIST_UNAVAILABLE_BODY =
  "One or more packages exceed safe manual-handling limits for Loading/Unloading Assistance. You can continue with the Air12 cargo estimate without this add-on.";
